import React, { createContext, useContext, useState, useEffect } from "react";

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    try {
      const localWishlist = localStorage.getItem("wishlist");
      if (localWishlist) {
        setWishlist(JSON.parse(localWishlist));
      }
    } catch (error) {
      console.error("Error loading wishlist from localStorage:", error);
    }
    setLoading(false);
  }, []);

  // Save to localStorage whenever wishlist changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
    }
  }, [wishlist, loading]);

  const addToWishlist = (productId) => {
    try {
      const newWishlist = [...wishlist, productId];
      setWishlist(newWishlist);
      localStorage.setItem("wishlist", JSON.stringify(newWishlist));
      return true;
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      return false;
    }
  };

  const removeFromWishlist = (productId) => {
    try {
      const newWishlist = wishlist.filter((id) => id !== productId);
      setWishlist(newWishlist);
      localStorage.setItem("wishlist", JSON.stringify(newWishlist));
      return true;
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      return false;
    }
  };

  const isInWishlist = (productId) => {
    return wishlist.includes(productId);
  };

  const clearWishlist = () => {
    try {
      setWishlist([]);
      localStorage.removeItem("wishlist");
      return true;
    } catch (error) {
      console.error("Error clearing wishlist:", error);
      return false;
    }
  };

  const value = {
    wishlist,
    loading,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
    wishlistCount: wishlist.length,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
